/**
 * Tenant themes and logos shipped with the bundle (`content/tenants/<slug>/`), used before sign-in and whenever the
 * database has no theme for the tenant. Anonymous visitors cannot read `tenant` under RLS, so the bundled copy is
 * what paints the sign-in screen.
 */
import { tenantLogoSize, type TenantLogoSize } from '@teslyn/domain';

export interface BundledTenant {
  readonly slug: string;
  readonly name: string;
  readonly productTitle: string;
  /** Title in the client area; null means "<name> Client Portal". */
  readonly clientTitle: string | null;
  readonly theme: unknown;
  readonly logoLight: string | null;
  readonly logoDark: string | null;
  /** Square icon for the browser tab; null keeps Teslyn's favicon. */
  readonly favicon: string | null;
  /** The theme file's `logo_size` (decision 524), medium when it has none. */
  readonly logoSize: TenantLogoSize;
}

interface ThemeFile {
  readonly tenant?: string;
  readonly display_name?: string;
  readonly product_title?: string;
  readonly client_portal_title?: string;
  readonly overrides?: unknown;
  readonly logos?: { readonly light?: string; readonly dark?: string; readonly favicon?: string };
  readonly logo_size?: unknown;
}

const THEMES = import.meta.glob('../../../../content/tenants/*/theme.json', {
  import: 'default',
  eager: true,
}) as Record<string, ThemeFile>;

const LOGOS = import.meta.glob('../../../../content/tenants/*/*.{png,svg}', {
  query: '?url',
  import: 'default',
  eager: true,
}) as Record<string, string>;

function slugOf(path: string): string {
  return path.split('/').at(-2) ?? '';
}

function logo(slug: string, file: string | undefined): string | null {
  if (!file) return null;
  const hit = Object.entries(LOGOS).find(([p]) => slugOf(p) === slug && p.endsWith(`/${file}`));
  return hit ? hit[1] : null;
}

export function bundledTenant(slug: string): BundledTenant | null {
  const entry = Object.entries(THEMES).find(([p]) => slugOf(p) === slug);
  if (!entry) return null;
  const t = entry[1];
  return {
    slug,
    name: t.display_name ?? slug,
    productTitle: t.product_title ?? 'Teslyn Cloud',
    clientTitle: t.client_portal_title ?? null,
    theme: t.overrides ?? {},
    logoLight: logo(slug, t.logos?.light),
    logoDark: logo(slug, t.logos?.dark),
    favicon: logo(slug, t.logos?.favicon),
    logoSize: tenantLogoSize(null, t),
  };
}
